import {
  ColorableWidget,
  DashboardBuilderChartWidget,
  DashboardBuilderHeaderWidget,
  DashboardBuilderPowerBiIframeWidget,
  DashboardBuilderNoteWidget,
  DashboardBuilderWidget,
  DashboardFilterSupportedField,
  NonEmptyWidget,
  WidgetLinkedDashboardFilter,
} from './types';
import {
  DEFAULT_BAR_CHART_PRIMARY_COLOR,
  DEFAULT_BAR_CHART_SECONDARY_COLOR,
} from '../consts';
import {
  Aggregate,
  AnyWidgetAttributes,
  APIBarChartAttributes,
  APIBubbleChartAttributes,
  APIChartWidgetAttributes,
  APIDashboardFilterParameter,
  APIFieldType,
  APILineChartAttributes,
  APINumberWidgetAttributes,
  APIPercentageChartAttributes,
  APIPieChartAttributes,
  APIReportFilters,
  APIStackedBarChartAttributes,
  APITableWidgetAttributes,
  BarChartColorOptions,
  BarChartConfiguration,
  BubbleChartWidgetConfiguration,
  ChartOrderOptions,
  DashboardFilterValues,
  DataSourceField,
  DataSourceType,
  HeaderWidgetAttributes,
  HexColor,
  PowerBiIframeWidgetAttributes,
  isGremlinSearch,
  LineChartConfiguration,
  NoteWidgetAttributes,
  NumberWidgetConfiguration,
  NumberWidgetTrend,
  NumberWidgetTrendRangeType,
  PercentageChartConfiguration,
  PercentageWidgetData,
  PieChartConfiguration,
  ReportColumnType,
  RollingDateRange,
  StackedBarChartConfiguration,
  SupportedDashboardFilterReferenceType,
  TableWidgetConfiguration,
  WidgetConfiguration,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import { intersection, isEmpty, overSome } from 'lodash';
import { isJust, Maybe } from '@ardoq/common-helpers';
import {
  BubbleChartViewSettingsOverrides,
  DashboardFilterParameter,
  FullscreenWidgetData,
  LoadedDashboard,
} from '../types';

const isWidgetWithData = (
  widgetData: DashboardBuilderChartWidget | APIChartWidgetAttributes
): widgetData is NonEmptyWidget<APIChartWidgetAttributes> =>
  !isEmptyWidgetData(widgetData);

const isHeaderWidgetData = (
  widget: unknown
): widget is DashboardBuilderHeaderWidget =>
  (widget as HeaderWidgetAttributes)?.viewType === WidgetTypes.HEADER;

const isNoteWidgetData = (
  widget: unknown
): widget is DashboardBuilderNoteWidget =>
  (widget as NoteWidgetAttributes)?.viewType === WidgetTypes.NOTE;

const isPowerBiIframeWidgetData = (
  widget: unknown
): widget is DashboardBuilderPowerBiIframeWidget =>
  (widget as PowerBiIframeWidgetAttributes)?.viewType ===
  WidgetTypes.POWER_BI_IFRAME;

const isReadPowerBiIframeWidgetData = (
  widget: unknown
): widget is PowerBiIframeWidgetAttributes =>
  (widget as PowerBiIframeWidgetAttributes)?.viewType ===
  WidgetTypes.POWER_BI_IFRAME;

const isStaticContentWidgetData = (
  widget: unknown
): widget is
  | DashboardBuilderNoteWidget
  | DashboardBuilderHeaderWidget
  | DashboardBuilderPowerBiIframeWidget =>
  (widget as NoteWidgetAttributes)?.viewType === WidgetTypes.NOTE ||
  (widget as HeaderWidgetAttributes)?.viewType === WidgetTypes.HEADER ||
  (widget as PowerBiIframeWidgetAttributes)?.viewType ===
    WidgetTypes.POWER_BI_IFRAME;

const isReadNoteWidgetData = (
  // TODO: this function and the one for the reader are duplicated. Clean up.
  widget: unknown
): widget is NoteWidgetAttributes =>
  (widget as NoteWidgetAttributes)?.viewType === WidgetTypes.NOTE;

const isReadHeaderWidgetData = (
  widget: unknown
): widget is HeaderWidgetAttributes =>
  (widget as HeaderWidgetAttributes)?.viewType === WidgetTypes.HEADER;

const isChartWidgetData = (
  widgetData: unknown
): widgetData is DashboardBuilderChartWidget =>
  Boolean(widgetData && !isStaticContentWidgetData(widgetData));

const isNonEmptyWidgetData = <T extends APIChartWidgetAttributes>(
  widgetData: T
): widgetData is NonEmptyWidget<T> => isJust(widgetData.data);

const isTableWidgetData = (
  widgetData: unknown
): widgetData is TableWidgetConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.TABLE;

const isPieChartData = (
  widgetData: unknown
): widgetData is PieChartConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.PIE_CHART;

const isPercentageChartData = (
  widgetData: unknown
): widgetData is PercentageChartConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.PERCENTAGE;

const isStackedBarChartData = (
  widgetData: unknown
): widgetData is StackedBarChartConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType ===
  WidgetTypes.STACKED_BAR_CHART;

const isLineChartData = (
  widgetData: unknown
): widgetData is LineChartConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.LINE_CHART;

const isNumberWidgetData = (
  widgetData: unknown
): widgetData is NumberWidgetConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.NUMBER;

const isBarChartData = (
  widgetData: unknown
): widgetData is BarChartConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.BAR_CHART;

/** Chart displaying label -> aggregated number. */
const isCategoricalChartData = (
  widgetData: unknown
): widgetData is BarChartConfiguration | PieChartConfiguration =>
  // Currently, only non-trends included (b/c we don't support custom aggregates
  // For stacked bar chart (yet))
  isBarChartData(widgetData) || isPieChartData(widgetData);

const isBubbleChartData = (
  widgetData: unknown
): widgetData is BubbleChartWidgetConfiguration =>
  (widgetData as WidgetConfiguration)?.viewType === WidgetTypes.BUBBLE_CHART;

const isCategoricalChartType = (widgetType?: WidgetTypes) =>
  widgetType === WidgetTypes.BAR_CHART || widgetType === WidgetTypes.PIE_CHART;

const hasMultipleFields = overSome([
  isCategoricalChartData,
  isLineChartData,
  isTableWidgetData,
]) as (widgetData: unknown) => widgetData is WidgetConfiguration;

// Narrow read types
const isReadWidgetData = (
  widgetData: unknown
): widgetData is APIChartWidgetAttributes =>
  Boolean(widgetData) && 'data' in (widgetData as APIChartWidgetAttributes);

const isReadTableWidgetData = (
  widgetData: unknown
): widgetData is APITableWidgetAttributes =>
  isReadWidgetData(widgetData) && isTableWidgetData(widgetData);

const isReadPieChartData = (
  widgetData: unknown
): widgetData is APIPieChartAttributes =>
  isReadWidgetData(widgetData) && isPieChartData(widgetData);

const isReadBubbleChartData = (
  widgetData: unknown
): widgetData is APIBubbleChartAttributes =>
  isReadWidgetData(widgetData) && isBubbleChartData(widgetData);

const isReadPercentageChartData = (
  widgetData: unknown
): widgetData is APIPercentageChartAttributes =>
  isReadWidgetData(widgetData) && isPercentageChartData(widgetData);

const isReadStackedBarChartData = (
  widgetData: unknown
): widgetData is APIStackedBarChartAttributes =>
  isReadWidgetData(widgetData) && isStackedBarChartData(widgetData);

const isReadLineChartData = (
  widgetData: unknown
): widgetData is APILineChartAttributes =>
  isReadWidgetData(widgetData) && isLineChartData(widgetData);

const isReadNumberWidgetData = (
  widgetData: unknown
): widgetData is APINumberWidgetAttributes =>
  isReadWidgetData(widgetData) &&
  typeof widgetData.data === 'number' &&
  isNumberWidgetData(widgetData);

const isReadBarChartData = (
  widgetData: unknown
): widgetData is APIBarChartAttributes =>
  isReadWidgetData(widgetData) && isBarChartData(widgetData);

const isColorableWidget = (widget: unknown): widget is ColorableWidget =>
  isReadPieChartData(widget) ||
  isReadStackedBarChartData(widget) ||
  isReadLineChartData(widget) ||
  (isReadBarChartData(widget) &&
    widget.colorSelection === BarChartColorOptions.COLOR_THEME);

const isValidHeaderWidgetData = (
  widget: DashboardBuilderHeaderWidget
): boolean => Boolean(widget.content.trim().length);

const isValidNoteWidgetData = (widget: DashboardBuilderNoteWidget): boolean =>
  !isEmpty(widget.content.trim());

const isEmptyWidgetData = (
  widget: DashboardBuilderChartWidget | APIChartWidgetAttributes
) => {
  if (widget.data === null) return true;
  if (
    widget.viewType === WidgetTypes.GAUGE &&
    (widget.data as PercentageWidgetData).denominator === 0
  ) {
    return true;
  }
  return false;
};

const getBarChartSegmentColor = (
  segmentName: string,
  isOtherSegment: boolean,
  colorProps:
    | {
        colorSelection: BarChartColorOptions.SINGLE_COLOR;
        primaryColor: HexColor;
        secondaryColor: HexColor;
      }
    | {
        colorSelection: BarChartColorOptions.COLOR_THEME;
        colors: Record<string, string>;
      }
): HexColor => {
  if (colorProps.colorSelection === BarChartColorOptions.SINGLE_COLOR) {
    return isOtherSegment ? colorProps.secondaryColor : colorProps.primaryColor;
  } else if (colorProps.colors[segmentName]) {
    return colorProps.colors[segmentName];
  }
  return isOtherSegment
    ? DEFAULT_BAR_CHART_SECONDARY_COLOR
    : DEFAULT_BAR_CHART_PRIMARY_COLOR;
};

const hasConfigurableSlices = (
  widgetData: DashboardBuilderChartWidget
): widgetData is
  | BarChartConfiguration
  | PieChartConfiguration
  | StackedBarChartConfiguration =>
  isStackedBarChartData(widgetData) || isCategoricalChartData(widgetData);

// aggregate
const defaultAggregate = (
  widgetData: DashboardBuilderChartWidget
): Aggregate | undefined => {
  if (isCategoricalChartData(widgetData)) {
    return Aggregate.COUNT;
  }
  return undefined;
};

const isTimeLineData = (
  widgetData: DashboardBuilderChartWidget
): widgetData is StackedBarChartConfiguration | LineChartConfiguration =>
  isStackedBarChartData(widgetData) || isLineChartData(widgetData);

const isOrderSelectionValid = (
  orderSelection?: ChartOrderOptions,
  fieldType?: APIFieldType
): boolean => {
  return !(
    !fieldType ||
    (orderSelection === ChartOrderOptions.LIST_FIELD &&
      fieldType !== APIFieldType.LIST) ||
    (fieldType === APIFieldType.LIST &&
      orderSelection !== ChartOrderOptions.LIST_FIELD)
  );
};

const maybeResetSortingIfPossible = (
  widgetData: TableWidgetConfiguration,
  sortableFields: DataSourceField[]
): string => {
  const selectedSortableFields = intersection(
    widgetData.fields,
    sortableFields.map(field => field.name)
  );

  const sortedByFieldWasRemoved = !widgetData.fields.includes(widgetData.sort);
  if (sortedByFieldWasRemoved) {
    return selectedSortableFields.length
      ? selectedSortableFields[0]
      : widgetData.fields[0]; // this can lead to widget having sort by non-sortable field, but it works w/o errors
  }
  const isCurrentSortValid = selectedSortableFields.some(
    field => field === widgetData.sort
  );
  if (!isCurrentSortValid && selectedSortableFields.length)
    return selectedSortableFields[0];
  return widgetData.sort;
};

const hasTrendEnabled = (
  widgetData: NumberWidgetConfiguration
): widgetData is NumberWidgetConfiguration & { trend: NumberWidgetTrend } =>
  Boolean(widgetData.trend);

const hasRollingTrend = (
  widgetData: NumberWidgetConfiguration & { trend: NumberWidgetTrend }
): widgetData is NumberWidgetConfiguration & {
  trend: NumberWidgetTrend & RollingDateRange;
} => Boolean(widgetData.trend.rangeType === NumberWidgetTrendRangeType.ROLLING);

const isSurveyBased = (
  widgetData: APIChartWidgetAttributes | DashboardBuilderChartWidget
): boolean =>
  widgetData.datasource?.sourceType === WidgetDataSourceTypes.SURVEY;

const isChartTypeSupportDashboardFilters = (
  widgetData: unknown
): widgetData is
  | PieChartConfiguration
  | BarChartConfiguration
  | TableWidgetConfiguration
  | NumberWidgetConfiguration
  | PercentageChartConfiguration =>
  (isCategoricalChartData(widgetData) ||
    isTableWidgetData(widgetData) ||
    isNumberWidgetData(widgetData) ||
    isPercentageChartData(widgetData)) &&
  widgetData.datasource.sourceType === WidgetDataSourceTypes.REPORT;

const isChartSupportDashboardFilters = (
  widgetData: unknown,
  dashboardFilterSupportedFields?: DashboardFilterSupportedField[]
): widgetData is
  | PieChartConfiguration
  | BarChartConfiguration
  | TableWidgetConfiguration
  | NumberWidgetConfiguration
  | PercentageChartConfiguration => {
  if (!widgetOperations.isChartTypeSupportDashboardFilters(widgetData)) {
    return false;
  }

  return !isEmpty(dashboardFilterSupportedFields);
};

const isChartSupportLinkModeDashboardFilter = (
  widgetData: unknown,
  linkModeDashboardFilterParameter:
    | DashboardFilterParameter
    | APIDashboardFilterParameter,
  dashboardFilterSupportedFields?: DashboardFilterSupportedField[]
): widgetData is
  | PieChartConfiguration
  | BarChartConfiguration
  | TableWidgetConfiguration
  | NumberWidgetConfiguration
  | PercentageChartConfiguration => {
  if (
    !widgetOperations.isChartSupportDashboardFilters(
      widgetData,
      dashboardFilterSupportedFields
    )
  ) {
    return false;
  }

  return linkModeDashboardFilterParameter?.fieldApiName &&
    dashboardFilterSupportedFields
    ? dashboardFilterSupportedFields.some(
        field =>
          field.key === linkModeDashboardFilterParameter.fieldApiName &&
          field.type === linkModeDashboardFilterParameter.type
      )
    : true;
};

const isReferenceColumnDashboardFilterType = (
  type: APIFieldType | SupportedDashboardFilterReferenceType | undefined
): boolean =>
  type === ReportColumnType.REFERENCE_TYPE_INCOMING ||
  type === ReportColumnType.REFERENCE_TYPE_OUTGOING;

const getWidgetMaskMessageForDashboardFilters = ({
  widgetData,
  linkModeDashboardFilterParameter,
  isLinkModeDashboardFilterFieldUsedInWidgetFilter,
  isLinkModeDashboardFilterFieldUsedInWidgetCondition,
  isLinkToLinkModeDashboardFilterSupported,
  isLinkedToLinkModeDashboardFilter,
  datasourceStrategy,
}: {
  widgetData: APIChartWidgetAttributes | DashboardBuilderChartWidget;
  linkModeDashboardFilterParameter: DashboardFilterParameter;
  isLinkToLinkModeDashboardFilterSupported: boolean;
  isLinkedToLinkModeDashboardFilter: boolean;
  isLinkModeDashboardFilterFieldUsedInWidgetFilter: boolean;
  isLinkModeDashboardFilterFieldUsedInWidgetCondition: boolean;
  datasourceStrategy?: DataSourceType;
}): string | undefined => {
  if (isLineChartData(widgetData))
    return "Dashboard filters aren't supported for line charts";
  if (isStackedBarChartData(widgetData))
    return "Dashboard filters aren't supported for stacked bar charts";
  if (isSurveyBased(widgetData))
    return "Dashboard filters aren't supported for charts based on surveys";
  if (isLinkModeDashboardFilterFieldUsedInWidgetCondition)
    return `Column "${linkModeDashboardFilterParameter.fieldLabel}" is used by the percentage condition. Remove the condition first.`;
  if (
    !linkModeDashboardFilterParameter.fieldApiName &&
    !isLinkToLinkModeDashboardFilterSupported
  )
    return `Can't filter this chart. This chart's report doesn't include any fields supporting the dashboard filter`;
  if (linkModeDashboardFilterParameter.fieldApiName) {
    if (
      isLinkToLinkModeDashboardFilterSupported &&
      isLinkModeDashboardFilterFieldUsedInWidgetFilter
    )
      return `This chart is already filtered by "${linkModeDashboardFilterParameter.fieldLabel}". Click Review link to replace it with the dashboard filter`;
    if (
      !isLinkToLinkModeDashboardFilterSupported &&
      isGremlinSearch(datasourceStrategy) &&
      isReferenceColumnDashboardFilterType(
        linkModeDashboardFilterParameter.type
      )
    )
      return isLinkedToLinkModeDashboardFilter
        ? `Reference column filters are not supported for graph search reports. Unlink the filter`
        : `Can't link this filter to chart. Reference column filters are only supported for advanced search reports`;
    if (
      !isLinkToLinkModeDashboardFilterSupported &&
      isLinkedToLinkModeDashboardFilter
    )
      return `Column ${linkModeDashboardFilterParameter.fieldLabel} is missing from the report or its type has changed. Unlink the filter`;
    if (!isLinkToLinkModeDashboardFilterSupported)
      return `Can't link this filter to chart. This chart's report doesn't include the ${linkModeDashboardFilterParameter.fieldLabel} field that this filter uses`;
  }
  return undefined;
};

const mergeDashboardAndWidgetFilters = (
  widgetFilters: APIReportFilters,
  activeLinkedDashboardFilters: WidgetLinkedDashboardFilter[]
): APIReportFilters => {
  if (isEmpty(activeLinkedDashboardFilters)) return widgetFilters;

  const dashboardFiltersToMerge =
    activeLinkedDashboardFilters.reduce<APIReportFilters>(
      (acc, dashboardFilter) => {
        if (
          dashboardFilter.values &&
          !isEmpty(dashboardFilter.values) &&
          !dashboardFilter.warning
        ) {
          acc[dashboardFilter.fieldApiName] = {
            values: dashboardFilter.values,
          };
        }
        return acc;
      },
      {}
    );

  return { ...widgetFilters, ...dashboardFiltersToMerge };
};

const isInaccessibleWidgetForDashboardFilters = (
  widget: AnyWidgetAttributes | DashboardBuilderWidget
): boolean =>
  widgetOperations.isChartTypeSupportDashboardFilters(widget) &&
  !(widget.permissions?.canEditWidget ?? false);

const getWidgetLinkedDashboardFilters = ({
  widgetData,
  globalFilterValues,
  dashboardFilterParameters = [],
}: {
  widgetData: APIChartWidgetAttributes | DashboardBuilderChartWidget;
  globalFilterValues?: DashboardFilterValues;
  dashboardFilterParameters: APIDashboardFilterParameter[];
}): WidgetLinkedDashboardFilter[] => {
  if (
    isEmpty(widgetData.globalFilterBindings) ||
    isEmpty(dashboardFilterParameters)
  ) {
    return [];
  }

  const linkedDashboardFilterParameterIds = Object.values(
    widgetData.globalFilterBindings ?? {}
  );
  const linkedDashboardFilters = !isEmpty(linkedDashboardFilterParameterIds)
    ? dashboardFilterParameters
        .filter(
          parameter =>
            linkedDashboardFilterParameterIds.includes(parameter.id) &&
            parameter.fieldApiName &&
            parameter.type
        )
        .map(parameter => ({
          id: parameter.id,
          name: parameter.name,
          type: parameter.type!,
          fieldApiName: parameter.fieldApiName!,
          values: globalFilterValues?.[parameter.id],
          warning: widgetData.globalFilterWarnings
            ? widgetData.globalFilterWarnings[parameter.id]
            : undefined,
        }))
    : [];

  return linkedDashboardFilters;
};

const getActiveWidgetLinkedDashboardFilters = ({
  widgetData,
  dashboardData,
}: {
  widgetData: Maybe<FullscreenWidgetData>;
  dashboardData: Maybe<LoadedDashboard>;
}): WidgetLinkedDashboardFilter[] => {
  const hasDashboardData = !!dashboardData;
  const isChartWidget = widgetOperations.isChartWidgetData(widgetData);
  const hasActiveDashboardFilters =
    hasDashboardData &&
    !isEmpty(dashboardData.globalFilterValues) &&
    !isEmpty(dashboardData.globalFilterParameters);

  if (hasDashboardData && isChartWidget && hasActiveDashboardFilters) {
    const allLinkedFilters = widgetOperations.getWidgetLinkedDashboardFilters({
      widgetData,
      globalFilterValues: dashboardData.globalFilterValues,
      dashboardFilterParameters: dashboardData.globalFilterParameters,
    });

    return allLinkedFilters.filter(
      linkedFilter => !isEmpty(linkedFilter.values)
    );
  }

  return [];
};

const getWidgetLinkModeDashboardFilterProps = (
  widget: DashboardBuilderChartWidget,
  linkModeDashboardFilterParameter: APIDashboardFilterParameter | undefined,
  dashboardFilterSupportedFields: DashboardFilterSupportedField[]
) => {
  const isLinkToLinkModeDashboardFilterSupported =
    !isEmpty(dashboardFilterSupportedFields) && linkModeDashboardFilterParameter
      ? widgetOperations.isChartSupportLinkModeDashboardFilter(
          widget,
          linkModeDashboardFilterParameter,
          dashboardFilterSupportedFields
        )
      : false;

  const isLinkModeDashboardFilterFieldUsedInWidgetFilter =
    !!linkModeDashboardFilterParameter &&
    widgetOperations.isDashboardFilterFieldUsedInWidgetFilter(
      widget,
      linkModeDashboardFilterParameter
    );

  const isLinkedToLinkModeDashboardFilter = Boolean(
    widget.globalFilterBindings &&
    Object.values(widget.globalFilterBindings).find(
      linkedParameterId =>
        linkedParameterId === linkModeDashboardFilterParameter?.id
    )
  );

  const isLinkModeDashboardFilterFieldUsedInWidgetCondition =
    !!linkModeDashboardFilterParameter?.fieldApiName &&
    widgetOperations.isPercentageChartData(widget) &&
    !!widget.condition &&
    linkModeDashboardFilterParameter.fieldApiName in widget.condition;

  return {
    isLinkToLinkModeDashboardFilterSupported,
    isLinkModeDashboardFilterFieldUsedInWidgetFilter,
    isLinkModeDashboardFilterFieldUsedInWidgetCondition,
    isLinkedToLinkModeDashboardFilter,
  };
};

const isDashboardFilterFieldUsedInWidgetFilter = (
  widgetData: APIChartWidgetAttributes | DashboardBuilderChartWidget,
  dashboardFilterParameter: APIDashboardFilterParameter
): boolean => {
  const widgetFilters = widgetData.filters;
  const filterForField =
    dashboardFilterParameter.fieldApiName && widgetFilters
      ? widgetFilters[dashboardFilterParameter.fieldApiName]
      : undefined;

  return Boolean(filterForField && !isEmpty(filterForField.values));
};

const toBubbleChartViewSettingsOverrides = (
  widgetData: BubbleChartWidgetConfiguration
): BubbleChartViewSettingsOverrides => ({
  selectedFieldNameX: widgetData.fields[0] ?? '',
  selectedFieldNameY: widgetData.fields[1] ?? '',
  selectedFieldNameRadius: widgetData.fields[2] ?? '',
  background: widgetData.background,
  customQuadrantSettings: widgetData.customQuadrantSettings,
  showBubblesWithZeroValue: widgetData.showBubblesWithZeroValue,
  selectedComponentTypes: widgetData.selectedComponentTypeIds,
});

export const widgetOperations = {
  isWidgetWithData,
  defaultAggregate,
  getBarChartSegmentColor,
  getWidgetMaskMessageForDashboardFilters,
  hasConfigurableSlices,
  hasMultipleFields,
  hasRollingTrend,
  hasTrendEnabled,
  isBarChartData,
  isBubbleChartData,
  isCategoricalChartData,
  isCategoricalChartType,
  isChartTypeSupportDashboardFilters,
  isChartSupportDashboardFilters,
  isChartSupportLinkModeDashboardFilter,
  isChartWidgetData,
  isColorableWidget,
  isEmptyWidgetData,
  isHeaderWidgetData,
  isPowerBiIframeWidgetData,
  isInaccessibleWidgetForDashboardFilters,
  isNoteWidgetData,
  isPercentageChartData,
  isStaticContentWidgetData,
  isReadNoteWidgetData,
  isLineChartData,
  isNonEmptyWidgetData,
  isNumberWidgetData,
  isOrderSelectionValid,
  isPieChartData,
  isReadBarChartData,
  isReadBubbleChartData,
  isReadHeaderWidgetData,
  isReadPowerBiIframeWidgetData,
  isReadLineChartData,
  isReadNumberWidgetData,
  isReadPercentageChartData,
  isReadPieChartData,
  isReadStackedBarChartData,
  isReadTableWidgetData,
  isReadWidgetData,
  isReferenceColumnDashboardFilterType,
  isStackedBarChartData,
  isTableWidgetData,
  isTimeLineData,
  isValidHeaderWidgetData,
  isValidNoteWidgetData,
  isDashboardFilterFieldUsedInWidgetFilter,
  maybeResetSortingIfPossible,
  mergeDashboardAndWidgetFilters,
  getWidgetLinkedDashboardFilters,
  getActiveWidgetLinkedDashboardFilters,
  getWidgetLinkModeDashboardFilterProps,
  toBubbleChartViewSettingsOverrides,
};
